import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hrApi } from '../../api/hr.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function EmployeeDirectory() {
  const { can } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ department: '', status: '', search: '' });
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canCreate = can('employee', 'create');

  useEffect(() => {
    hrApi.listDepartments().then((d) => setDepartments(d?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    hrApi
      .listEmployees(filters)
      .then((res) => setEmployees(res?.items || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [filters]);

  const filtered = useMemo(() => employees, [employees]);

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>Employee Directory</h1>
          <p className="muted">{employees.length} employees</p>
        </div>
        <div className="actions">
          <div className="seg">
            <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}>Grid</button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
          </div>
          {canCreate && (
            <Link to="/hr/employees/new" className="btn primary">+ New Employee</Link>
          )}
        </div>
      </header>

      <div className="hr-grid">
        <aside className="hr-filters card">
          <h3>Filter</h3>
          <label>
            Search
            <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Name, email, code…" />
          </label>
          <label>
            Department
            <select value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}>
              <option value="">All</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">Any</option>
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
              <option value="terminated">Terminated</option>
              <option value="resigned">Resigned</option>
            </select>
          </label>
        </aside>

        <div className="hr-list">
          {error && <div className="alert error">{error}</div>}
          {loading ? (
            <div className="muted">Loading…</div>
          ) : view === 'grid' ? (
            <div className="card-grid" data-testid="employee-grid" data-loaded="true">
              {filtered.map((e) => (
                <Link
                  key={e.id}
                  to={`/hr/employees/${e.id}`}
                  className="employee-card"
                  data-testid="employee-card"
                  data-employee-id={e.id}
                  data-employee-code={e.employee_code}
                >
                  <div className="avatar" role="img" aria-label={`${e.user_name || 'employee'}'s avatar`}>{(e.user_name || '?').charAt(0).toUpperCase()}</div>
                  <div className="info">
                    <div className="name">{e.user_name}</div>
                    <div className="muted small">{e.position_title || '—'}</div>
                    <div className="muted small">{e.department_name || 'No department'}</div>
                    <div className="chips small">
                      <span className="chip">{e.employee_code}</span>
                      <span className={`chip status-${e.status}`}>{e.status}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id}>
                    <td><Link to={`/hr/employees/${e.id}`}>{e.employee_code}</Link></td>
                    <td>{e.user_name}</td>
                    <td className="muted">{e.user_email}</td>
                    <td>{e.department_name || '—'}</td>
                    <td>{e.position_title || '—'}</td>
                    <td><span className={`chip status-${e.status}`}>{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && filtered.length === 0 && <div className="muted center">No employees match.</div>}
        </div>
      </div>
    </section>
  );
}
